// Treasury → wallets directory (the /treasury INDEX). Search by email /
// display name / user_id, filter by status + currency, click into a row
// for the per-wallet detail surface (balance + manual credit + freeze +
// transaction / deposit / withdrawal history).
//
// Routing note: this is an index route, not the section layout. It used
// to be `treasury.tsx` — the layout itself — rendering the list behind a
// `location.pathname !== "/treasury"` guard. That worked for rendering
// and failed for SEARCH: a `validateSearch` on a layout route is
// inherited by every route beneath it, so this page's `?currency=`
// silently became a parameter of /treasury/payouts, /treasury/accounts
// and every /treasury/revenue/* page too. Moving the list into its own
// index route is what actually scopes the param to the page that owns
// it; the layout next door is now a bare Outlet, and the section
// follows the same shape Revenue and Fees already use.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { fetchWallets, keys } from "@/lib/queries";
import { campaignCurrency, type CurrencyCode } from "@/lib/currency";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { useScopedPage } from "@/components/activity/useScopedPage";
import { fmtRelative, statusLabel } from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { CurrencyChip } from "@/components/currency/columns";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import {
  WalletCurrencyFilter,
  parseWalletCurrencySearch,
  walletCurrencySearchFor,
  walletListCurrency,
  type WalletCurrencySearch,
} from "@/components/treasury/WalletCurrencyFilter";

interface WalletFilters {
  status: string;
}

const DEFAULT_SORT = { by: "updated_at", dir: "desc" } as const;
const PAGE_LIMIT = 20;

/**
 * The currency filter lives in the URL rather than in useTableState like
 * Status does, because it is the one filter this page opens with a value
 * for (GOLD — see WalletCurrencyFilter). A default that only exists in
 * component state is a default an operator cannot get back to, cannot
 * link to, and loses on every back-button press.
 *
 * Declared on the INDEX route, not on the /treasury layout. A layout
 * route's search params belong to every route under it, so declaring it
 * one level up would hand `?currency=` to Payouts, System accounts and
 * the whole Revenue group — where a child may narrow the type but never
 * widen it, and where a value this page wrote would follow the operator
 * across pages that mean something different by the same word.
 */
export const Route = createFileRoute("/_app/treasury/")({
  validateSearch: (raw: Record<string, unknown>): { currency?: WalletCurrencySearch } => {
    const currency = parseWalletCurrencySearch(raw.currency);
    return currency ? { currency } : {};
  },
  // The prefetch has to name the same currency the component will ask
  // for, or the loader warms a key nothing reads and the operator sees a
  // skeleton on a page that was supposed to be ready.
  loaderDeps: ({ search }) => ({ currency: walletListCurrency(search.currency) }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: keys.walletsList(undefined, PAGE_LIMIT, 0, DEFAULT_SORT, {
        currency: deps.currency,
      }),
      queryFn: () =>
        fetchWallets({
          limit: PAGE_LIMIT,
          offset: 0,
          sort: DEFAULT_SORT,
          currency: deps.currency,
        }),
    }),
  component: TreasuryListPage,
});

function TreasuryListPage() {
  const [q, setQ] = useState("");
  const table = useTableState<WalletFilters>({
    defaultSort: DEFAULT_SORT,
    defaultFilters: { status: "" },
  });
  const search = Route.useSearch();
  const navigate = useNavigate();
  // undefined = the combined list; otherwise the one currency shown.
  const currency = walletListCurrency(search.currency);
  const setCurrency = useCallback(
    (next: CurrencyCode | undefined) =>
      // Merged rather than replaced so the page's other URL state (and
      // anything a future filter adds here) survives a currency pick.
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, currency: walletCurrencySearchFor(next) }),
        replace: true,
      }),
    [navigate],
  );
  // Narrowing the search resets pagination — same pattern as users.tsx.
  useEffect(() => {
    table.setPage(0);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  // The currency narrows the result set the same way a filter does, but
  // it lives in the URL rather than in useTableState, so it needs the
  // page reset spelled out: a page-5 operator who picks a currency must
  // land back on page 1 rather than past the end of a shorter list.
  useScopedPage(currency ?? "", table.setPage);

  const trimmed = q.trim();
  const filtersForKey = {
    status: table.filters.status || undefined,
    currency,
  };
  const wallets = useQuery({
    queryKey: keys.walletsList(trimmed, table.limit, table.offset, table.sort, filtersForKey),
    queryFn: () =>
      fetchWallets({
        limit: table.limit,
        offset: table.offset,
        q: trimmed || undefined,
        sort: table.sort,
        status: table.filters.status || undefined,
        currency,
      }),
  });
  // Named for the empty state, which has to say WHICH currency came back
  // with nothing — "No wallets match" under a picker reading Gold reads
  // as a broken page rather than an answer.
  const currencyName = useCurrencyName(currency);
  const showReset = table.hasActiveFilters || q !== "" || search.currency !== undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Treasury</h1>
        <p className="text-sm text-muted">Search wallets by email, display name, or user_id. One row per currency a user holds — pick the currency below. Click a row for balance + history + operator actions.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Input placeholder="search email, display name, or user_id…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="w-40">
          <Select
            label="Status"
            placeholder="Status — any"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "FROZEN", label: "Frozen" },
              { value: "CLOSED", label: "Closed" },
            ]}
          />
        </div>
        <div className="w-52">
          {/* SM-494: since ledger 0025 a user has one wallet row per
              currency, so the directory shows one currency at a time and
              opens on GOLD. The options carry each currency's configured
              name, symbol and artwork rather than its wire code — the
              same identity the chips in the table below use. */}
          <WalletCurrencyFilter value={currency} onChange={setCurrency} />
        </div>
        {showReset && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ("");
              table.reset();
              // Back to the GOLD default, not to the combined list: reset
              // means "as the page opened".
              void navigate({
                to: ".",
                search: (prev) => ({ ...prev, currency: undefined }),
                replace: true,
              });
            }}
          >
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Wallets</CardTitle>
          <span className="text-2xs text-muted">{wallets.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {wallets.isLoading && <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>}
          {wallets.data && (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className="px-5 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">User</th>
                  {/* A wallet is one (user, currency) row since ledger
                      0025, so the currency is part of the row's
                      identity — not decoration, and never omitted. */}
                  <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Currency</th>
                  <SortableHeader label="Available" field="available" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Reserved" field="reserved" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Status" field="status" sort={table.sort} onSortChange={table.setSort} />
                  <SortableHeader label="Updated" field="updated_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                </tr>
              </thead>
              <tbody>
                {wallets.data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-6 text-center text-2xs text-muted">
                      {currency
                        ? `No ${currencyName} wallets match the current filter.`
                        : "No wallets match the current filter."}
                    </td>
                  </tr>
                )}
                {wallets.data.items.map((w) => (
                  <tr
                    key={w.wallet_id}
                    className="border-b border-border/60 transition-colors last:border-0 hover:bg-surface-2/60"
                  >
                    <td className="px-5 py-2.5">
                      <Link
                        to="/treasury/$id"
                        params={{ id: w.user_id }}
                        // Three rows per user, one page each: the currency
                        // is what tells them apart.
                        search={{ currency: campaignCurrency(w.currency) }}
                        className="block leading-tight hover:text-accent"
                      >
                        <div className="text-xs font-medium text-fg">{w.email || w.display_name || w.user_id}</div>
                        <div className="font-mono text-2xs text-muted">
                          {w.display_name && w.email ? `${w.display_name} · ` : ""}
                          {w.user_id}
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-2.5"><CurrencyChip currency={w.currency} /></td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <CurrencyAmount currency={w.currency} value={w.available} />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <CurrencyAmount currency={w.currency} value={w.reserved} tone="warning" />
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={w.status === "ACTIVE" ? "success" : "danger"}>{statusLabel(w.status)}</Badge>
                    </td>
                    <td className="px-5 py-2.5 text-right text-2xs text-muted">{fmtRelative(w.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          {wallets.data && (
            <Pagination
              page={wallets.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="wallets"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
